import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { PRODUCTS, GOLD_PRODUCTS, COMPANY } from "@/data/site";
import { Reveal, Overline } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import PricingModal from "@/components/PricingModal";

const TABS = [
  { id: "all", label: "All Products", testid: "product-tab-all" },
  { id: "beverages", label: "Beverages", testid: "product-tab-beverages" },
  { id: "snacks", label: "Snacks", testid: "product-tab-snacks" },
];

export default function Products() {
  const [params] = useSearchParams();
  const [brand, setBrand] = useState(params.get("brand") === "gold" ? "gold" : "refresh");
  const [tab, setTab] = useState("all");
  const [pricingFor, setPricingFor] = useState(null);
  const list = PRODUCTS.filter((p) => tab === "all" || p.category === tab);

  return (
    <div className="pt-40">
      <section className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Overline>Trade Catalogue</Overline>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Built for the shelf.
            <span className="block text-sun-bright">Priced for the trade.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
            Two house brands, one supply partner. Ananya Refresh covers foods and beverages; Ananya
            Gold covers personal and home care. Pricing is shared directly with verified trade
            partners.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="inline-flex flex-wrap gap-2 rounded-full border border-white/10 bg-navy-card p-1.5" data-testid="brand-switcher">
            <button
              data-testid="brand-tab-refresh"
              onClick={() => setBrand("refresh")}
              className={`relative rounded-full px-6 py-3 text-sm font-bold transition-colors duration-300 ${
                brand === "refresh" ? "text-navy" : "text-slate-300 hover:text-white"
              }`}
            >
              {brand === "refresh" && (
                <motion.span layoutId="brand-pill" className="absolute inset-0 rounded-full bg-lime" transition={{ duration: 0.35 }} />
              )}
              <span className="relative">Ananya Refresh <span className="hidden sm:inline font-medium opacity-70">· Foods & Beverages</span></span>
            </button>
            <button
              data-testid="brand-tab-gold"
              onClick={() => setBrand("gold")}
              className={`relative rounded-full px-6 py-3 text-sm font-bold transition-colors duration-300 ${
                brand === "gold" ? "text-navy" : "text-slate-300 hover:text-white"
              }`}
            >
              {brand === "gold" && (
                <motion.span layoutId="brand-pill" className="absolute inset-0 rounded-full bg-sun-bright" transition={{ duration: 0.35 }} />
              )}
              <span className="relative">Ananya Gold <span className="hidden sm:inline font-medium opacity-70">· Personal & Home Care</span></span>
            </button>
          </div>
        </Reveal>

        {brand === "refresh" ? (
          <>
            <Reveal delay={0.15} className="mt-8">
              <div className="inline-flex flex-wrap gap-2 rounded-full border border-white/10 bg-navy-card p-1.5">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    data-testid={t.testid}
                    onClick={() => setTab(t.id)}
                    className={`relative rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${
                      tab === t.id ? "text-navy" : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {tab === t.id && (
                      <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-lime" transition={{ duration: 0.35 }} />
                    )}
                    <span className="relative">{t.label}</span>
                  </button>
                ))}
              </div>
            </Reveal>
            <motion.div layout className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <AnimatePresence mode="popLayout">
                {list.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} onRequest={setPricingFor} />
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        ) : (
          <>
            <Reveal delay={0.15} className="mt-8">
              <div
                data-testid="gold-range-banner"
                className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-sun/30 bg-sun/5 px-6 py-4"
              >
                <p className="text-sm text-slate-300">
                  Ananya Gold is the personal &amp; home care brand of{" "}
                  <span className="font-bold text-sun-bright">{COMPANY.name}</span>
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="/downloads/ananya-gold-trade-sheet.pdf"
                    download
                    data-testid="gold-trade-sheet-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-sun-bright px-5 py-2.5 text-xs font-bold text-navy transition-colors hover:bg-lime"
                  >
                    <Download size={14} /> Download Trade Sheet
                  </a>
                  <Link
                    to="/consumer-goods"
                    data-testid="gold-company-link"
                    className="group inline-flex items-center gap-1.5 text-sm font-bold text-sun-bright transition-all hover:gap-2.5"
                  >
                    About the company <ArrowUpRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
            <motion.div layout className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="gold-products-grid">
              <AnimatePresence mode="popLayout">
                {GOLD_PRODUCTS.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} onRequest={setPricingFor} />
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </section>

      <PricingModal product={pricingFor} open={!!pricingFor} onOpenChange={(o) => !o && setPricingFor(null)} />
    </div>
  );
}
